import React from 'react';
import { education } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { GraduationCap, Calendar, MapPin, BookOpen, Award } from 'lucide-react';

const Education = () => {
  return (
    <section id="education" className="py-20 bg-[#0a0a0b]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">Education</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto rounded-full"></div>
        </div>

        {/* Education Card */}
        <div className="max-w-4xl mx-auto">
          <Card className="bg-[#1a1a1b] border-gray-800 hover:border-blue-500/50 transition-all duration-300 overflow-hidden">
            <CardHeader className="border-b border-gray-800 bg-gradient-to-r from-blue-950/20 to-cyan-950/20">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={32} className="text-white" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-2xl text-white mb-2">
                    {education.degree}
                  </CardTitle>
                  <div className="space-y-2">
                    <p className="text-lg text-blue-400 font-semibold">
                      {education.university}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <MapPin size={16} />
                        {education.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={16} />
                        Expected Graduation: {education.expectedGraduation}
                      </span>
                    </div>
                    <Badge className="bg-gradient-to-r from-green-600 to-emerald-600 text-white border-none">
                      {education.status}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-8 space-y-8">
              {/* Description */}
              <div>
                <p className="text-gray-300 leading-relaxed">
                  {education.description}
                </p>
              </div>

              {/* Academic Performance */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                  <Award size={20} className="text-cyan-400" />
                  Academic Performance
                </h3>
                <p className="text-gray-300">{education.gpa}</p>
              </div>

              {/* Relevant Coursework */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <BookOpen size={20} className="text-blue-400" />
                  Relevant Coursework
                </h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {education.relevantCoursework.map((course, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 text-gray-300"
                    >
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 mt-2 flex-shrink-0"></div>
                      <span>{course}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Achievements */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Award size={20} className="text-cyan-400" />
                  Key Achievements
                </h3>
                <div className="space-y-3">
                  {education.achievements.map((achievement, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 bg-[#0a0a0b] rounded-lg border border-gray-800"
                    >
                      <Award size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">{achievement}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Education;
